import { z } from "zod"
import { ShopifyMetafieldsConfigSchema } from "./metafieldsConfig"

export const UpsertShopifyIntegration = z.object({
  shopifyHash: z.string(),
  accessToken: z.string(),
  shopifyUrl: z.string(),
  prefix: z.string().nullish(),
  suffix: z.string().nullish(),
  appVersion: z.string().nullish(),
  isOrderFulfilledWebhookActive: z.boolean().optional(),
  isAbandonCheckoutWebhookActive: z.boolean().optional(),
  metafieldsConfig: ShopifyMetafieldsConfigSchema.optional(),
})

export const GetShopifyOrderInfoSchema = z.object({
  orderId: z.string(),
})

export const ShopifyFlowActionHandleEnum = {
  OrderFulfilled: "order-fulfilled",
  AbandonShoppingCart: "abandon-shopping-cart",
} as const

export const ShopifyFlowActionSchema = z.object({
  campaign_id: z.string().min(1).optional(),
  shopify_domain: z.string().min(1),
  handle: z.enum(ShopifyFlowActionHandleEnum),
  properties: z.record(z.string(), z.unknown()),
})

export const UseShopifyCampaignWidgetSchema = z.object({
  campaignId: z.string().transform((value) => BigInt(value)),
})

export const SearchShopifyProductsSchema = z.object({
  includeDraft: z
    .union([
      z.boolean(),
      z
        .enum(["true", "false"])
        .transform((value) => (typeof value === "string" ? value === "true" : value)),
    ])
    .optional()
    .default(false),
  query: z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, " ")),
  limit: z.coerce.number().int().min(1).max(50).optional(),
})

export const UpdateConvertSizeChartParamsSchema = z.object({
  id: z.string(),
})

export const UpdateConvertSizeChartMetafieldSchema = z
  .object({
    key: z.string().trim().min(1),
    type: z.string().trim().min(1),
    value: z.string().optional(),
    base64Image: z.string().optional(),
    jsonFile: z.string().optional(),
  })
  .refine(
    (value) =>
      value.value !== undefined || value.base64Image !== undefined || value.jsonFile !== undefined,
    {
      path: ["value"],
      message: "A value, base64 image, or JSON file is required",
    },
  )

export const UpdateConvertSizeChartSchema = z
  .object({
    metafields: z.array(UpdateConvertSizeChartMetafieldSchema).min(1).optional(),
    metafieldValue: z.string().optional(),
    variantId: z.string().trim().min(1).optional(),
  })
  .refine((value) => value.metafields !== undefined || value.metafieldValue !== undefined, {
    path: ["metafields"],
    message: "Metafields are required",
  })

export const ConvertSizeChartFileSchema = z.object({
  file: z.object({
    data: z.unknown().refine((value) => Buffer.isBuffer(value), {
      error: "Expected a Buffer",
    }),
    name: z.string(),
    mimetype: z.string(),
  }),
})

export const GenerateSizeChartImageSchema = z.object({
  variables: z.preprocess((value) => {
    if (Array.isArray(value)) return value
    if (typeof value === "string") return [value]
    if (value && typeof value === "object") return Object.values(value)
    return value
  }, z.array(z.string())),
})

export const CloneConvertSizeChartQuerySchema = z.object({
  imageMetafieldKey: z.string().trim().min(1).optional(),
  productId: z.string().trim().min(1),
  sizeChartMetafieldKey: z.string().trim().min(1).optional(),
  variantId: z.string().trim().min(1).optional(),
})
